import * as z from 'zod';
import { MAX_NOTES_LENGTH } from '../limits.js';
import { DEFAULT_PHONE_COUNTRY, isSupportedPhoneCountry, isValidPhone } from '../locale/phone.js';
import { bilingualHelpSchema, bilingualTextSchema, countryCodeSchema, emailSchema, isoDateSchema, machineKeySchema, strict, uuidSchema, versionSchema, } from './common.js';
/**
 * AD-16: core fields, admin-defined fields and their values are the same thing.
 *
 * One encryption path, one validation path, one AAD scheme, one projection path, one revision
 * format, and adding a field is data rather than a migration. What makes that work is that a
 * field's TYPE is a closed set of eleven, and each type carries a small, declarative validation
 * record that this module compiles into a Zod schema.
 *
 * **AD-17: a field's type is immutable.** The field id is inside the AAD of every value encrypted
 * under it, so a retype would strand every existing value as undecryptable-in-place. The update
 * schema below therefore has no `type` and no `key`: not "ignores them", does not accept them, and
 * a request carrying either is rejected because every object schema here is strict.
 *
 * **There is deliberately no admin-supplied regular expression.** A `pattern` on a text field looks
 * like the obvious eleventh validation option and is a denial of service authored through the
 * administration UI: a catastrophically backtracking pattern compiled server-side and run over
 * every submitted value takes the process down, and nothing in the UI would suggest that was
 * possible. Bounded lengths and the typed formats below cover the real cases.
 */
/** The eleven types. Closed, because each one is a rendering, a validator and a projection rule. */
export const FIELD_TYPES = [
    'text',
    'textarea',
    'number',
    'date',
    'boolean',
    'select',
    'multiselect',
    'phone',
    'email',
    'country',
    'passport',
];
export const fieldTypeSchema = z.enum(FIELD_TYPES);
/** `core` fields come from the brief and cannot be retired or retyped; `admin` ones are added. */
export const fieldKindSchema = z.enum(['core', 'admin']);
/** How hard the form pushes. `warn` is the non-blocking completeness warning, never a refusal. */
export const fieldRequirementSchema = z.enum(['optional', 'warn', 'required']);
export const fieldStatusSchema = z.enum(['active', 'retired']);
/**
 * The core field keys, from the brief.
 *
 * The definitions themselves, with their labels and requirement levels, are seeded by the data
 * layer beside the trigger that protects them. What lives here is the key set, because the
 * settings registry references it (`persons.warn_if_missing`) and a settings value naming a field
 * that does not exist would be a warning nobody ever sees.
 */
export const CORE_FIELD_KEYS = [
    'first_name',
    'last_name',
    'second_last_name',
    'phone_1',
    'phone_2',
    'email',
    'date_of_birth',
    'country_of_origin',
    'passport_number',
    'passport_expiry',
    'notes',
    // The intake set, appended in one release. **Appended rather than sorted into display order**,
    // and that is load-bearing: `SHIPPED_COMPLETENESS_KEYS` in `settings.ts` is a filter over THIS
    // array, so its order is the order of `persons.warn_if_missing`'s shipped default. Re-sorting
    // here to match `displayOrder` would change a settings default silently.
    'sex',
    'language_1',
    'language_2',
    'location',
    'admission_date',
    'discharge_date',
    'discharge_reason',
    'family_unit',
    'family_unit_ref',
    'possible_minor',
    'vulnerability_1',
    'vulnerability_2',
    'triage',
    'triage_date',
    'protection_formulated_triage',
    'return_agreement',
    'return_agreement_date',
    'protection_appointment',
    'protection_appointment_date',
    'protection_formulated_centre',
    'protection_registration',
    'protection_registration_date',
    'protection_formalization',
    'protection_formalization_date',
    // Appended for the same reason the intake set was, and the reason is in the comment above: this
    // array's order is `SHIPPED_COMPLETENESS_KEYS`' filter order, so sorting it into display order
    // would change a settings default in silence. `reference_code` renders between `language_2` and
    // `location`, which is a property of its `displayOrder` and of nothing here.
    'reference_code',
];
/**
 * The one token in the completeness warning that is not a field.
 *
 * Images live in their own table rather than in `person_field_values`, but the brief lists them
 * alongside the fields an operator should be warned about, so the warning list accepts this token
 * and the warning service resolves it against `person_images`.
 */
export const IMAGES_COMPLETENESS_KEY = 'images';
/**
 * The sections the shipped fields are grouped into, in the order the form renders them.
 *
 * **This is presentation, and `section` stays free text.** The column admits any string up to 64
 * characters and an administrator may invent one on their own field, which is why both
 * `search/fields.ts` and `persons/duplicates.ts` refuse to treat a section as a semantic handle.
 * What this list is for is the only thing a section legitimately decides: a heading. A known
 * section is rendered from the catalogue, in the reader's language; anything else renders the
 * administrator's own string, because there is nowhere to translate it from.
 *
 * The order here is not consulted at render time — grouping follows `displayOrder`, so an
 * administrator reordering the form reorders the groups with it — but it records the intended
 * shape, which is that the three fields a registration cannot be saved without live in the first
 * two groups.
 */
export const PERSON_FIELD_SECTIONS = [
    'identity',
    'stay',
    'contact',
    'document',
    'vulnerability',
    'protection',
    'other',
];
/** Whether a stored section string is one this product ships a heading for. */
export function isKnownSection(value) {
    return PERSON_FIELD_SECTIONS.includes(value);
}
/** A single-line text value is one line. Long text is what `textarea` is for. */
export const MAX_TEXT_LENGTH = 500;
/** One choice in a select, labelled in both languages so the form renders in either. */
export const selectOptionSchema = strict({
    value: machineKeySchema,
    label: bilingualTextSchema,
});
/**
 * The choices of a select, with the duplicate check pointing at the row that caused it.
 *
 * **Its own code, and a path.** This used to borrow `errors.field.key_taken` — "a field with the key
 * «{{fieldKey}}» already exists" — which is a sentence about a different thing, carrying a
 * placeholder nothing here could fill, shown to an administrator who had just typed one choice
 * twice. A `superRefine` rather than a `refine` because a refinement that reports the CONTAINER
 * leaves the browser to guess which of twenty rows to mark.
 */
const optionsSchema = z
    .array(selectOptionSchema)
    .min(1, { error: 'errors.field.options_required' })
    .max(200)
    .superRefine((options, ctx) => {
    const seen = new Set();
    for (const [index, option] of options.entries()) {
        if (seen.has(option.value)) {
            ctx.addIssue({
                code: 'custom',
                message: 'errors.field.option_value_duplicate',
                path: [index, 'value'],
            });
        }
        seen.add(option.value);
    }
});
/**
 * The per-type validation record, stored as the `validation` JSON of a field definition.
 *
 * A discriminated union rather than an open bag: a `maxSelected` on a boolean field, or a
 * `notFuture` on a text field, is a definition somebody mis-authored, and it should fail when the
 * field is defined rather than be ignored for a year and then start mattering.
 */
export const fieldValidationSchema = z.discriminatedUnion('type', [
    strict({
        type: z.literal('text'),
        minLength: z.int().min(0).max(MAX_TEXT_LENGTH).optional(),
        maxLength: z.int().min(1).max(MAX_TEXT_LENGTH).optional(),
    }),
    strict({
        type: z.literal('textarea'),
        maxLength: z.int().min(1).max(MAX_NOTES_LENGTH).optional(),
    }),
    strict({
        type: z.literal('number'),
        min: z.number().optional(),
        max: z.number().optional(),
        integer: z.boolean().optional(),
    }),
    strict({
        type: z.literal('date'),
        /** A date of birth cannot be tomorrow. */
        notFuture: z.boolean().optional(),
        /** A document expiry that has already passed is a warning elsewhere, not a refusal here. */
        notPast: z.boolean().optional(),
        /** Nobody registered at this facility was born before this year. */
        minYear: z.int().min(1800).max(2200).optional(),
        /** An age above this is a typo, not a person. */
        maxAgeYears: z.int().min(1).max(150).optional(),
    }),
    strict({ type: z.literal('boolean') }),
    strict({ type: z.literal('select'), options: optionsSchema }),
    strict({
        type: z.literal('multiselect'),
        options: optionsSchema,
        minSelected: z.int().min(0).max(200).optional(),
        maxSelected: z.int().min(1).max(200).optional(),
    }),
    strict({
        type: z.literal('phone'),
        // Refused when the field is DEFINED rather than ignored at every value: the country list this
        // product accepts is wider than the set of regions the numbering-plan metadata can parse for,
        // and a default country the parser does not know would silently do nothing.
        defaultCountry: countryCodeSchema
            .refine(isSupportedPhoneCountry, { error: 'errors.validation.invalid_country' })
            .optional(),
    }),
    strict({ type: z.literal('email') }),
    strict({ type: z.literal('country') }),
    strict({
        type: z.literal('passport'),
        minLength: z.int().min(1).max(64).optional(),
        maxLength: z.int().min(1).max(64).optional(),
    }),
]);
/** A field definition as the API returns it. */
export const fieldDefinitionSchema = strict({
    id: uuidSchema,
    key: machineKeySchema,
    kind: fieldKindSchema,
    type: fieldTypeSchema,
    label: bilingualTextSchema,
    help: bilingualHelpSchema.nullable(),
    requirement: fieldRequirementSchema,
    validation: fieldValidationSchema,
    isUnique: z.boolean(),
    isSearchable: z.boolean(),
    displayOrder: z.int(),
    section: z.string().max(64).nullable(),
    status: fieldStatusSchema,
    version: versionSchema,
});
/** Creating an admin-defined field. `kind` is not accepted: an added field is always `admin`. */
export const createFieldRequestSchema = strict({
    key: machineKeySchema,
    type: fieldTypeSchema,
    label: bilingualTextSchema,
    help: bilingualHelpSchema.optional(),
    requirement: fieldRequirementSchema.default('optional'),
    validation: fieldValidationSchema,
    isUnique: z.boolean().default(false),
    isSearchable: z.boolean().default(false),
    displayOrder: z.int().min(0).max(9999),
    section: z.string().max(64).optional(),
}).refine((request) => request.validation.type === request.type, {
    error: 'errors.validation.invalid_option',
    path: ['validation', 'type'],
});
/**
 * Amending a field.
 *
 * `key` and `type` are absent, and because the object is strict a request carrying either is
 * refused rather than silently ignored. That is AD-17 expressed where a client meets it, and the
 * database trigger says the same thing again for anything that bypasses this schema.
 */
export const updateFieldRequestSchema = strict({
    label: bilingualTextSchema.optional(),
    help: bilingualHelpSchema.nullable().optional(),
    requirement: fieldRequirementSchema.optional(),
    validation: fieldValidationSchema.optional(),
    isUnique: z.boolean().optional(),
    isSearchable: z.boolean().optional(),
    displayOrder: z.int().min(0).max(9999).optional(),
    section: z.string().max(64).nullable().optional(),
    status: fieldStatusSchema.optional(),
    version: versionSchema,
});
function yearsBetween(fromIso, toIso) {
    const from = Date.parse(`${fromIso}T00:00:00Z`);
    const to = Date.parse(`${toIso}T00:00:00Z`);
    return (to - from) / (365.2425 * 24 * 60 * 60 * 1000);
}
function dateValueSchema(validation, context) {
    return isoDateSchema.superRefine((value, ctx) => {
        if (validation.notFuture === true && value > context.today) {
            ctx.addIssue({ code: 'custom', message: 'errors.validation.date_in_future' });
        }
        if (validation.notPast === true && value < context.today) {
            ctx.addIssue({ code: 'custom', message: 'errors.validation.invalid_date' });
        }
        if (validation.minYear !== undefined && Number(value.slice(0, 4)) < validation.minYear) {
            ctx.addIssue({ code: 'custom', message: 'errors.validation.date_too_old' });
        }
        if (validation.maxAgeYears !== undefined &&
            yearsBetween(value, context.today) > validation.maxAgeYears) {
            ctx.addIssue({ code: 'custom', message: 'errors.validation.date_too_old' });
        }
    });
}
function textValueSchema(validation) {
    // No declared message on either bound: `errors/details.ts` reads the issue's own origin, so a
    // string bound resolves to the character-counting sentence and carries the number with it. A
    // declaration here could only restate that, and would be the thing that went stale if the bound
    // ever moved to a different kind of value.
    let schema = z
        .string()
        .trim()
        .max(validation.maxLength ?? MAX_TEXT_LENGTH);
    if (validation.minLength !== undefined) {
        schema = schema.min(validation.minLength);
    }
    return schema;
}
function numberValueSchema(validation) {
    let schema = validation.integer === true ? z.int() : z.number();
    if (validation.min !== undefined) {
        schema = schema.min(validation.min);
    }
    if (validation.max !== undefined) {
        schema = schema.max(validation.max);
    }
    return schema;
}
function multiselectValueSchema(validation) {
    const permitted = validation.options.map((option) => option.value);
    let schema = z.array(z.string().refine((value) => permitted.includes(value), {
        error: 'errors.validation.invalid_option',
    }));
    if (validation.minSelected !== undefined) {
        schema = schema.min(validation.minSelected);
    }
    if (validation.maxSelected !== undefined) {
        schema = schema.max(validation.maxSelected);
    }
    return schema;
}
/**
 * A telephone value, parsed for the region the field declares.
 *
 * This is what makes `defaultCountry` mean something. Without it an operator entering the Moroccan
 * national form `0612345678` into a field defined with `defaultCountry: 'MA'` would have it read as
 * Spanish, found not to be a Spanish number, and refused, while the definition said it should be
 * accepted.
 */
function phoneValueSchema(validation) {
    const defaultCountry = validation.defaultCountry ?? DEFAULT_PHONE_COUNTRY;
    return z.string().refine((value) => isValidPhone(value, defaultCountry), {
        error: 'errors.validation.invalid_phone',
    });
}
function passportValueSchema(validation) {
    // Alphanumeric, with the separators a document actually prints. Deliberately permissive about
    // format and strict about length: passport numbering differs by issuing state, and a pattern
    // tight enough to be useful would lock out a real document at a gate.
    return z
        .string()
        .trim()
        .toUpperCase()
        .min(validation.minLength ?? 4)
        .max(validation.maxLength ?? 20)
        .regex(/^[A-Z0-9][A-Z0-9 -]*[A-Z0-9]$/u, { error: 'errors.validation.invalid_passport' });
}
/**
 * Compiles a field definition into the validator for one submitted value.
 *
 * The same function is used by the API and by the browser form, which is the point of the contracts
 * package: a value the form accepted cannot be one the API rejects, and vice versa.
 */
export function fieldValueSchema(definition, context) {
    const { validation } = definition;
    switch (validation.type) {
        case 'text': {
            return textValueSchema(validation);
        }
        case 'textarea': {
            return z.string().max(validation.maxLength ?? MAX_NOTES_LENGTH);
        }
        case 'number': {
            return numberValueSchema(validation);
        }
        case 'date': {
            return dateValueSchema(validation, context);
        }
        case 'boolean': {
            return z.boolean();
        }
        case 'select': {
            const permitted = validation.options.map((option) => option.value);
            return z.string().refine((value) => permitted.includes(value), {
                error: 'errors.validation.invalid_option',
            });
        }
        case 'multiselect': {
            return multiselectValueSchema(validation);
        }
        case 'phone': {
            return phoneValueSchema(validation);
        }
        case 'email': {
            return emailSchema;
        }
        case 'country': {
            return countryCodeSchema;
        }
        case 'passport': {
            return passportValueSchema(validation);
        }
    }
}
//# sourceMappingURL=field.js.map